import { useMemo, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useAdmin } from '@/hooks/useAdmin'
import { Navigate } from 'react-router-dom'
import { addDays, format, parseISO } from 'date-fns'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  useAdminAccessibilityOverlaysReport,
  useAdminListingsReport,
  type ListingsReportStats,
  type OverlayReportStats,
} from '@/hooks/useAdminListingsReport'
import { Link } from 'react-router-dom'
import { ADMIN_PORTAL_PATH } from '@/lib/adminPortal'

function VisualDateField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  const selected = value ? parseISO(value) : null
  return (
    <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
      {label}
      <DatePicker
        selected={selected}
        onChange={(date) => {
          if (!date) return
          onChange(format(date, 'yyyy-MM-dd'))
        }}
        dateFormat="yyyy-MM-dd"
        className="block mt-1 rounded px-2 py-1 border"
        popperPlacement="bottom-start"
        style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}
      />
    </label>
  )
}

function StatsTable({
  columns,
  rows,
  ariaLabel,
}: {
  columns: { key: string; label: string; align?: 'right' }[]
  rows: Record<string, string | number>[]
  ariaLabel: string
}) {
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="min-w-full text-sm border-collapse" aria-label={ariaLabel}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--bg-tertiary)' }}>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={`py-2 px-3 text-left font-semibold ${c.align === 'right' ? 'text-right' : ''}`}
                style={{ color: 'var(--text-secondary)' }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--bg-tertiary)' }}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`py-2 px-3 ${c.align === 'right' ? 'text-right tabular-nums' : ''}`}
                  style={{ color: 'var(--text-primary)' }}
                >
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function shiftIsoDateUtc(ymd: string, dayDelta: number): string {
  return format(addDays(parseISO(`${ymd}T00:00:00.000Z`), dayDelta), 'yyyy-MM-dd')
}

function statsToAccessibilityColumns(stats: ListingsReportStats) {
  return {
    total: stats.total,
    ad: stats.withAd,
    subs: stats.withSubtitles,
    bsl: stats.withBsl,
    anyAcc: stats.withAnyAccessibility,
    none: stats.withNoAccessibility,
    xml: stats.fromXml,
    overlay: stats.fromOverlay,
  }
}

export default function AdminListingsReportPage() {
  const { user } = useAuth()
  const isAdmin = useAdmin()
  const today = useMemo(() => new Date(), [])
  const defaultStart = useMemo(
    () => format(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0)), 'yyyy-MM-dd'),
    [today]
  )
  const defaultEnd = useMemo(() => {
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0))
    end.setUTCDate(end.getUTCDate() + 90)
    return format(end, 'yyyy-MM-dd')
  }, [today])
  const [overlayStartDate, setOverlayStartDate] = useState(defaultStart)
  const [overlayEndDate, setOverlayEndDate] = useState(defaultEnd)
  const [listingsStartDate, setListingsStartDate] = useState(defaultStart)
  const [listingsEndDate, setListingsEndDate] = useState(defaultEnd)
  const [channelViewDate, setChannelViewDate] = useState(defaultStart)
  const applyTodayToListings = () => {
    setListingsStartDate(defaultStart)
    setListingsEndDate(defaultStart)
  }
  const applyTodayToOverlays = () => {
    setOverlayStartDate(defaultStart)
    setOverlayEndDate(defaultStart)
  }
  const { data, isLoading, error, refetch, isFetching } = useAdminListingsReport(
    Boolean(user && isAdmin),
    listingsStartDate,
    listingsEndDate
  )
  const overlaysReport = useAdminAccessibilityOverlaysReport(Boolean(user && isAdmin), overlayStartDate, overlayEndDate)

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />
  }

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6" role="status" aria-live="polite">
        <p style={{ color: 'var(--text-secondary)' }}>Loading listings report…</p>
      </div>
    )
  }

  if (error) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-6" aria-labelledby="listings-report-error">
        <h1 id="listings-report-error" className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Listings report
        </h1>
        <p role="alert" style={{ color: 'var(--color-error)' }}>
          {error instanceof Error ? error.message : 'Failed to load report.'}
        </p>
      </section>
    )
  }

  const report = data!

  const dayColumns = [
    { key: 'day', label: 'Day (UTC)' },
    { key: 'total', label: 'Titles', align: 'right' as const },
    { key: 'ad', label: 'AD', align: 'right' as const },
    { key: 'subs', label: 'Subtitles', align: 'right' as const },
    { key: 'bsl', label: 'BSL', align: 'right' as const },
    { key: 'anyAcc', label: 'Any accessibility', align: 'right' as const },
    { key: 'none', label: 'No flags', align: 'right' as const },
    { key: 'xml', label: 'Source: XML', align: 'right' as const },
    { key: 'overlay', label: 'Source: overlay', align: 'right' as const },
  ]

  const dayRows = report.byDay.map((d) => {
    const s = statsToAccessibilityColumns(d.stats)
    return {
      day: format(parseISO(`${d.dayKey}T12:00:00.000Z`), 'EEE d MMM yyyy'),
      ...s,
    }
  })

  const channelColumns = [
    { key: 'channel', label: 'Channel' },
    { key: 'id', label: 'ID' },
    { key: 'total', label: 'Titles', align: 'right' as const },
    { key: 'ad', label: 'AD', align: 'right' as const },
    { key: 'subs', label: 'Subtitles', align: 'right' as const },
    { key: 'bsl', label: 'BSL', align: 'right' as const },
    { key: 'anyAcc', label: 'Any accessibility', align: 'right' as const },
    { key: 'none', label: 'No flags', align: 'right' as const },
    { key: 'xml', label: 'XML', align: 'right' as const },
    { key: 'overlay', label: 'Overlay', align: 'right' as const },
  ]

  const channelDateInListingsWindow =
    channelViewDate >= listingsStartDate && channelViewDate <= listingsEndDate
  const channelRows = (report.byChannelByDayUtc[channelViewDate] ?? []).map((c) => {
    const s = statsToAccessibilityColumns(c.stats)
    return {
      channel: c.displayName,
      id: c.channelId,
      ...s,
    }
  })

  const o = statsToAccessibilityColumns(report.overall)
  const overlayStatsToColumns = (stats: OverlayReportStats) => ({
    total: stats.total,
    ad: stats.withAd,
    subs: stats.withSubtitles,
    bsl: stats.withBsl,
    anyAcc: stats.withAnyAccessibility,
    none: stats.withNoAccessibility,
  })
  const overlayDayColumns = [
    { key: 'day', label: 'Day (UTC)' },
    { key: 'total', label: 'Rows', align: 'right' as const },
    { key: 'ad', label: 'AD', align: 'right' as const },
    { key: 'subs', label: 'Subtitles', align: 'right' as const },
    { key: 'bsl', label: 'BSL', align: 'right' as const },
    { key: 'anyAcc', label: 'Any accessibility', align: 'right' as const },
    { key: 'none', label: 'No flags', align: 'right' as const },
  ]
  const overlayChannelColumns = [
    { key: 'channel', label: 'Channel' },
    { key: 'id', label: 'ID' },
    { key: 'total', label: 'Rows', align: 'right' as const },
    { key: 'ad', label: 'AD', align: 'right' as const },
    { key: 'subs', label: 'Subtitles', align: 'right' as const },
    { key: 'bsl', label: 'BSL', align: 'right' as const },
    { key: 'anyAcc', label: 'Any accessibility', align: 'right' as const },
    { key: 'none', label: 'No flags', align: 'right' as const },
  ]

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Listings report
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Counts from <code className="text-xs">tv_listings</code> for{' '}
            <strong style={{ color: 'var(--text-primary)' }}>{report.windowLabel}</strong>. One row = one programme slot.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <VisualDateField label="Listings start date (UTC)" value={listingsStartDate} onChange={setListingsStartDate} />
          <VisualDateField label="Listings end date (UTC)" value={listingsEndDate} onChange={setListingsEndDate} />
          <button
            type="button"
            onClick={applyTodayToListings}
            className="px-4 py-2 rounded text-sm font-semibold shrink-0 border"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="px-4 py-2 rounded text-sm font-semibold shrink-0"
            style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
          >
            {isFetching ? 'Refreshing…' : 'Refresh listings report'}
          </button>
        </div>
      </div>

      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        <Link to={`${ADMIN_PORTAL_PATH}/listings-management`} className="underline" style={{ color: 'var(--color-focus)' }}>
          Listings control
        </Link>{' '}
        — sync EPG.{' '}
        <Link to={`${ADMIN_PORTAL_PATH}/listings-channels`} className="underline" style={{ color: 'var(--color-focus)' }}>
          Channel control
        </Link>{' '}
        — displayed channels. AD / Subtitles / BSL columns count rows where that flag is true (a single title can appear in more than one column).
      </p>

      {/* Overall + accessibility summary */}
      <section
        className="mb-8 p-4 rounded-lg border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="overall-heading"
      >
        <h2 id="overall-heading" className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Overall &amp; accessibility breakdown
        </h2>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Total programme rows
            </dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.total}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              With audio description
            </dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.ad}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              With subtitles
            </dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.subs}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              With BSL
            </dt>
            <dd className="text-2xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.bsl}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              With any accessibility flag
            </dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.anyAcc}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              With no accessibility flags
            </dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.none}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              From EPG (xml)
            </dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.xml}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Overlay-only rows
            </dt>
            <dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>
              {o.overlay}
            </dd>
          </div>
        </dl>
      </section>

      <section
        className="mb-8 p-4 rounded-lg border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="by-day-heading"
      >
        <h2 id="by-day-heading" className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By day
        </h2>
        {dayRows.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No rows in <code className="text-xs">tv_listings</code> for this window.</p>
        ) : (
          <StatsTable columns={dayColumns} rows={dayRows} ariaLabel="Listings counts by calendar day UTC" />
        )}
      </section>

      <section
        className="mb-8 p-4 rounded-lg border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="by-channel-heading"
      >
        <h2 id="by-channel-heading" className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          By channel
        </h2>
        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          Programme counts per channel for a single UTC calendar day (same day boundary as &ldquo;By day&rdquo; above).
        </p>
        <div className="flex flex-wrap items-end gap-2 mb-4">
          <button
            type="button"
            onClick={() => setChannelViewDate((d) => shiftIsoDateUtc(d, -1))}
            className="px-3 py-2 rounded text-sm font-semibold border"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-label="Previous day"
          >
            −1 day
          </button>
          <VisualDateField label="Channel view date (UTC)" value={channelViewDate} onChange={setChannelViewDate} />
          <button
            type="button"
            onClick={() => setChannelViewDate((d) => shiftIsoDateUtc(d, 1))}
            className="px-3 py-2 rounded text-sm font-semibold border"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
            aria-label="Next day"
          >
            +1 day
          </button>
          <button
            type="button"
            onClick={() => setChannelViewDate(defaultStart)}
            className="px-3 py-2 rounded text-sm font-semibold border"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            Today
          </button>
        </div>
        {!channelDateInListingsWindow ? (
          <p role="status" style={{ color: 'var(--text-secondary)' }}>
            This day is outside the listings start/end range at the top of the page. Adjust those dates or pick a day in
            range to load channel data.
          </p>
        ) : channelRows.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No channel data for this day.</p>
        ) : (
          <StatsTable
            columns={channelColumns}
            rows={channelRows}
            ariaLabel={`Listings counts by canonical channel for UTC day ${channelViewDate}`}
          />
        )}
      </section>

      <section
        className="mb-8 p-4 rounded-lg border-2"
        style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--bg-tertiary)' }}
        aria-labelledby="overlays-heading"
      >
        <h2 id="overlays-heading" className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
          Accessibility overlays report
        </h2>
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Direct view of <code className="text-xs">accessibility_overlays</code> by date/channel/accessibility. Use this for dates outside the TV Guide 7-day window (for example, 25 March).
        </p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <VisualDateField label="Start date (UTC)" value={overlayStartDate} onChange={setOverlayStartDate} />
          <VisualDateField label="End date (UTC)" value={overlayEndDate} onChange={setOverlayEndDate} />
          <button
            type="button"
            onClick={applyTodayToOverlays}
            className="px-4 py-2 rounded text-sm font-semibold border"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => overlaysReport.refetch()}
            disabled={overlaysReport.isFetching}
            className="px-4 py-2 rounded text-sm font-semibold"
            style={{ backgroundColor: 'var(--bg-interactive)', color: 'var(--text-inverse)' }}
          >
            {overlaysReport.isFetching ? 'Refreshing overlays…' : 'Refresh overlays report'}
          </button>
        </div>

        {overlaysReport.error ? (
          <p role="alert" style={{ color: 'var(--color-error)' }}>
            {overlaysReport.error instanceof Error ? overlaysReport.error.message : 'Failed to load accessibility overlays report.'}
          </p>
        ) : overlaysReport.data ? (
          <>
            <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
              Window: <strong style={{ color: 'var(--text-primary)' }}>{overlaysReport.data.windowLabel}</strong> | Total overlay rows:{' '}
              <strong style={{ color: 'var(--text-primary)' }}>{overlaysReport.data.rowCount}</strong>
            </p>
            <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-4">
              {(() => {
                const so = overlayStatsToColumns(overlaysReport.data.overall)
                return (
                  <>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Rows</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.total}</dd></div>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>AD</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.ad}</dd></div>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Subtitles</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.subs}</dd></div>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>BSL</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.bsl}</dd></div>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>Any accessibility</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.anyAcc}</dd></div>
                    <div><dt className="text-sm" style={{ color: 'var(--text-secondary)' }}>No flags</dt><dd className="text-xl font-semibold tabular-nums" style={{ color: 'var(--text-primary)' }}>{so.none}</dd></div>
                  </>
                )
              })()}
            </dl>
            <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Overlays by day</h3>
            <StatsTable
              columns={overlayDayColumns}
              rows={overlaysReport.data.byDay.map((d) => ({
                day: format(parseISO(`${d.dayKey}T12:00:00.000Z`), 'EEE d MMM yyyy'),
                ...overlayStatsToColumns(d.stats),
              }))}
              ariaLabel="Accessibility overlay counts by day"
            />
            <h3 className="font-semibold mt-5 mb-2" style={{ color: 'var(--text-primary)' }}>Overlays by channel</h3>
            <StatsTable
              columns={overlayChannelColumns}
              rows={overlaysReport.data.byChannel.map((c) => ({
                channel: c.displayName,
                id: c.channelId,
                ...overlayStatsToColumns(c.stats),
              }))}
              ariaLabel="Accessibility overlay counts by channel"
            />
          </>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>Loading overlay report…</p>
        )}
      </section>
    </div>
  )
}
